import getenv from 'getenv';

import {
  ArticleGroup,
  ArticleType,
  LocalePrefixToURLPathsMap,
  isValidCanonicalField,
  type AnyPage,
  type ArticlePage,
  type CarDetailPage,
  type ChargepilotProductUpdateArticlePage,
  type ElectricityTariffBlogArticlePage,
  type EnergyKnowledgeCenterArticlePage,
  type EnergyKnowledgeCenterWebinarPage,
  type EnergyKnowledgeCenterWhitepaperPage,
  type EnergyNewsArticlePage,
  type EnergyPage,
  type EnergyReferenceArticlePage,
  type JobDetailPage,
  type KnowledgeCenterArticlePage,
  type KnowledgeCenterWebinarPage,
  type KnowledgeCenterWhitepaperPage,
  type NewsArticlePage,
  type Page,
  type ReferenceArticlePage,
  type SearchDetailPage,
  type ShopCategoryPage,
  type StyleguideArticlePage,
} from '@tmh-website/shared/cms';
import { LocalePrefix, LocaleToPrefixMap } from '@tmh-website/shared/const';
import { assertUnreachable } from '@tmh-website/shared/helpers';

import { cleanLink } from './link.service';

export function isErrorPage(page: AnyPage) {
  return page?.id === 0;
}

export function hasPageInAncestors(page: Page, pageId: number) {
  let currentPage = page.parent;
  while (currentPage) {
    if (currentPage?.id === pageId) {
      return true;
    }
    if (currentPage.parent) {
      currentPage = currentPage.parent;
    }
  }
  return false;
}

export function getArticleOverviewPath(type: ArticleGroup, locale: LocalePrefix): string {
  switch (type) {
    case ArticleGroup.Chargepilot:
      return `${LocalePrefixToURLPathsMap[locale].chargepilotProductUpdateArticle}`;
    case ArticleGroup.ElectricityTariffBlogArticle:
      return `${LocalePrefixToURLPathsMap[locale].electricityTariffBlogArticle}`;
    case ArticleGroup.EnergyNewsArticle:
      return `${LocalePrefixToURLPathsMap[locale].energyNews}`;
    case ArticleGroup.EnergyKnowledgeCenterArticle:
    case ArticleGroup.EnergyKnowledgeCenterWebinar:
    case ArticleGroup.EnergyKnowledgeCenterWhitepaper:
      return `${LocalePrefixToURLPathsMap[locale].energyKnowledgeCenter}`;
    case ArticleGroup.EnergyReferenceArticle:
      return `${LocalePrefixToURLPathsMap[locale].energyReferences}`;
    case ArticleGroup.KnowledgeCenter:
      return LocalePrefixToURLPathsMap[locale].knowledgeCenter;
    case ArticleGroup.News:
      return `${LocalePrefixToURLPathsMap[locale].ourCompany}/${LocalePrefixToURLPathsMap[locale].news}`;
    case ArticleGroup.Reference:
      return `${LocalePrefixToURLPathsMap[locale].ourCompany}/${LocalePrefixToURLPathsMap[locale].references}`;
    case ArticleGroup.Styleguide:
      return `${LocalePrefixToURLPathsMap[locale].styleguideArticle}`;
    default:
      return assertUnreachable(type) as string;
  }
}

export function isKnowledgeCenterArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.KnowledgeCenter, locale)}/${LocalePrefixToURLPathsMap[locale].article}/`
  );
}

export function isEnergyKnowledgeCenterArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.EnergyKnowledgeCenterArticle, locale)}/${
      LocalePrefixToURLPathsMap[locale].article
    }/`
  );
}

export function isEnergyKnowledgeCenterWebinarPath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.EnergyKnowledgeCenterWebinar, locale)}/${
      LocalePrefixToURLPathsMap[locale].article
    }/`
  );
}

export function isEnergyKnowledgeCenterWhitepaperPath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.EnergyKnowledgeCenterWhitepaper, locale)}/${
      LocalePrefixToURLPathsMap[locale].article
    }/`
  );
}

export function isKnowledgeCenterWebinarPath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.KnowledgeCenter, locale)}/${LocalePrefixToURLPathsMap[locale].webinar}/`
  );
}
export function isKnowledgeCenterWhitepaperPath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.KnowledgeCenter, locale)}/${LocalePrefixToURLPathsMap[locale].whitepaper}/`
  );
}
export function isNewsArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.News, locale)}/${LocalePrefixToURLPathsMap[locale].article}/`
  );
}

export function isEnergyNewsArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.EnergyNewsArticle, locale)}/${LocalePrefixToURLPathsMap[locale].article}/`
  );
}

export function isReferenceArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.Reference, locale)}/${LocalePrefixToURLPathsMap[locale].article}/`
  );
}

export function isEnergyReferenceArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.EnergyReferenceArticle, locale)}/${
      LocalePrefixToURLPathsMap[locale].article
    }/`
  );
}

export function isStyleguideArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(`/${getArticleOverviewPath(ArticleGroup.Styleguide, locale)}/`);
}
export function isChargepilotProductUpdateArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(`/${getArticleOverviewPath(ArticleGroup.Chargepilot, locale)}/`);
}
export function isCarDetailPath(path: string, locale: LocalePrefix) {
  return path.startsWith(`/${LocalePrefixToURLPathsMap[locale].car}/`);
}

export function isJobDetailPath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${LocalePrefixToURLPathsMap[locale].ourCompany}/${LocalePrefixToURLPathsMap[locale].career}/`
  );
}

export function isSearchDetailPath(path: string, locale: LocalePrefix) {
  return path.startsWith(`/${LocalePrefixToURLPathsMap[locale].search}`);
}

export function isKnowledgeCenterGroupPath(path: string, locale: LocalePrefix) {
  return path.startsWith(`/${getArticleOverviewPath(ArticleGroup.KnowledgeCenter, locale)}/`);
}
export function isNewsGroupPath(path: string, locale: LocalePrefix) {
  return path.startsWith(`/${getArticleOverviewPath(ArticleGroup.News, locale)}/`);
}
export function isReferenceGroupPath(path: string, locale: LocalePrefix) {
  return path.startsWith(`/${getArticleOverviewPath(ArticleGroup.Reference, locale)}/`);
}

export function isCMSPage(page: AnyPage): page is Page | ShopCategoryPage {
  return (page as Page)?.parent !== undefined;
}

export function isElectricityTariffBlogArticlePath(path: string, locale: LocalePrefix) {
  return path.startsWith(
    `/${getArticleOverviewPath(ArticleGroup.ElectricityTariffBlogArticle, locale)}/${
      LocalePrefixToURLPathsMap[locale].article
    }/`
  );
}

export type PageWithBlocks =
  | ChargepilotProductUpdateArticlePage
  | EnergyKnowledgeCenterArticlePage
  | EnergyNewsArticlePage
  | EnergyPage
  | EnergyReferenceArticlePage
  | KnowledgeCenterArticlePage
  | NewsArticlePage
  | Page
  | ReferenceArticlePage
  | ShopCategoryPage;

export function isPageWithBlocks(page: AnyPage): page is PageWithBlocks {
  return (isArticlePage(page) || isCMSPage(page)) && !isKnowledgeCenterWebinarPage(page);
}

export function isArticlePage(page: AnyPage): page is ArticlePage {
  return (
    isChargepilotProductUpdateArticlePage(page) ||
    isElectricityTariffBlogArticlePage(page) ||
    isEnergyKnowledgeCenterArticlePage(page) ||
    isEnergyKnowledgeCenterWebinarPage(page) ||
    isEnergyKnowledgeCenterWhitepaperPage(page) ||
    isEnergyNewsArticlePage(page) ||
    isEnergyReferenceArticlePage(page) ||
    isKnowledgeCenterArticlePage(page) ||
    isKnowledgeCenterWebinarPage(page) ||
    isKnowledgeCenterWhitepaperPage(page) ||
    isNewsArticlePage(page) ||
    isReferenceArticlePage(page) ||
    isStyleguideArticlePage(page)
  );
}

/**
 * Checks if the page is using the plain article template.
 */
export function isPlainArticlePage(
  page: AnyPage
): page is
  | EnergyKnowledgeCenterArticlePage
  | EnergyNewsArticlePage
  | KnowledgeCenterArticlePage
  | NewsArticlePage
  | StyleguideArticlePage {
  return (
    isNewsArticlePage(page) ||
    isEnergyNewsArticlePage(page) ||
    isKnowledgeCenterArticlePage(page) ||
    isEnergyKnowledgeCenterArticlePage(page) ||
    isStyleguideArticlePage(page) ||
    isElectricityTariffBlogArticlePage(page) ||
    isChargepilotProductUpdateArticlePage(page)
  );
}

export function isNewsArticlePage(page: AnyPage): page is NewsArticlePage {
  return !!page && isNewsArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isEnergyNewsArticlePage(page: AnyPage): page is EnergyNewsArticlePage {
  return !!page && isEnergyNewsArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isKnowledgeCenterArticlePage(page: AnyPage): page is KnowledgeCenterArticlePage {
  return !!page && isKnowledgeCenterArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isEnergyKnowledgeCenterArticlePage(page: AnyPage): page is EnergyKnowledgeCenterArticlePage {
  return !!page && isEnergyKnowledgeCenterArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isEnergyKnowledgeCenterWebinarPage(page: AnyPage): page is EnergyKnowledgeCenterWebinarPage {
  return !!page && isEnergyKnowledgeCenterWebinarPath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isEnergyKnowledgeCenterWhitepaperPage(page: AnyPage): page is EnergyKnowledgeCenterWhitepaperPage {
  return !!page && isEnergyKnowledgeCenterWhitepaperPath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isKnowledgeCenterWebinarPage(page: AnyPage): page is KnowledgeCenterWebinarPage {
  return !!page && isKnowledgeCenterWebinarPath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isKnowledgeCenterWhitepaperPage(page: AnyPage): page is KnowledgeCenterWhitepaperPage {
  return !!page && isKnowledgeCenterWhitepaperPath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isReferenceArticlePage(page: AnyPage): page is ReferenceArticlePage {
  return !!page && isReferenceArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isEnergyReferenceArticlePage(page: AnyPage): page is EnergyReferenceArticlePage {
  return !!page && isEnergyReferenceArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isStyleguideArticlePage(page: AnyPage): page is StyleguideArticlePage {
  return !!page && isStyleguideArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isChargepilotProductUpdateArticlePage(page: AnyPage): page is ChargepilotProductUpdateArticlePage {
  return !!page && isChargepilotProductUpdateArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isCarDetailPage(page: AnyPage): page is CarDetailPage {
  return (
    !!page &&
    isCarDetailPath(page.path ?? '', LocaleToPrefixMap[page.locale]) &&
    (page as CarDetailPage).manufacturer !== undefined &&
    (page as CarDetailPage).model !== undefined
  );
}

export function isJobDetailPage(page: AnyPage): page is JobDetailPage {
  return !!page && isJobDetailPath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isSearchDetailPage(page: AnyPage): page is SearchDetailPage {
  return !!page && isSearchDetailPath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function isElectricityTariffBlogArticlePage(page: AnyPage): page is ElectricityTariffBlogArticlePage {
  return !!page && isElectricityTariffBlogArticlePath(page.path ?? '', LocaleToPrefixMap[page.locale]);
}

export function getArticleType(page: AnyPage): ArticleType {
  if (isKnowledgeCenterArticlePage(page)) {
    return ArticleType.KnowledgeCenterArticle;
  } else if (isEnergyKnowledgeCenterArticlePage(page)) {
    return ArticleType.EnergyKnowledgeCenterArticle;
  } else if (isEnergyKnowledgeCenterWebinarPage(page)) {
    return ArticleType.EnergyKnowledgeCenterWebinar;
  } else if (isEnergyKnowledgeCenterWhitepaperPage(page)) {
    return ArticleType.EnergyKnowledgeCenterWhitepaper;
  } else if (isKnowledgeCenterWebinarPage(page)) {
    return ArticleType.KnowledgeCenterWebinar;
  } else if (isKnowledgeCenterWhitepaperPage(page)) {
    return ArticleType.KnowledgeCenterWhitepaper;
  } else if (isNewsArticlePage(page)) {
    return ArticleType.NewsArticle;
  } else if (isEnergyNewsArticlePage(page)) {
    return ArticleType.EnergyNewsArticle;
  } else if (isReferenceArticlePage(page)) {
    return ArticleType.ReferenceArticle;
  } else if (isEnergyReferenceArticlePage(page)) {
    return ArticleType.EnergyReferenceArticle;
  } else if (isStyleguideArticlePage(page)) {
    return ArticleType.StyleguideArticle;
  } else if (isChargepilotProductUpdateArticlePage(page)) {
    return ArticleType.ChargepilotProductUpdateArticle;
  } else if (isElectricityTariffBlogArticlePage(page)) {
    return ArticleType.ElectricityTariffBlogArticle;
  } else {
    throw new Error(`Invalid article page provided: ${page}`);
  }
}

export function getArticleGroup(page: AnyPage): ArticleGroup {
  if (
    isKnowledgeCenterArticlePage(page) ||
    isKnowledgeCenterWebinarPage(page) ||
    isKnowledgeCenterWhitepaperPage(page)
  ) {
    return ArticleGroup.KnowledgeCenter;
  } else if (isEnergyKnowledgeCenterArticlePage(page)) {
    return ArticleGroup.EnergyKnowledgeCenterArticle;
  } else if (isEnergyKnowledgeCenterWebinarPage(page)) {
    return ArticleGroup.EnergyKnowledgeCenterWebinar;
  } else if (isEnergyKnowledgeCenterWhitepaperPage(page)) {
    return ArticleGroup.EnergyKnowledgeCenterWhitepaper;
  } else if (isNewsArticlePage(page)) {
    return ArticleGroup.News;
  } else if (isEnergyNewsArticlePage(page)) {
    return ArticleGroup.EnergyNewsArticle;
  } else if (isReferenceArticlePage(page)) {
    return ArticleGroup.Reference;
  } else if (isEnergyReferenceArticlePage(page)) {
    return ArticleGroup.EnergyReferenceArticle;
  } else if (isStyleguideArticlePage(page)) {
    return ArticleGroup.Styleguide;
  } else if (isChargepilotProductUpdateArticlePage(page)) {
    return ArticleGroup.Chargepilot;
  } else if (isElectricityTariffBlogArticlePage(page)) {
    return ArticleGroup.ElectricityTariffBlogArticle;
  } else {
    throw new Error(`Invalid article page provided: ${page}`);
  }
}

export interface Breadcrumb {
  position: number;
  url: string;
  name: string;
}

/**
 * This will point to the canonical URL of the page, or to the page itself if no canonical page is set.
 * It will only return `null` if the page is an error page.
 */
export function getCanonicalUrl(page: AnyPage, customHost?: string): string | null {
  if (isErrorPage(page) || !page) {
    return null;
  }

  // The canonical path is the one of the current page by default
  let path = page.path;

  if (isValidCanonicalField(page.canonical)) {
    path = page.canonical.value;
  }

  const canonicalPath = `${LocaleToPrefixMap[page.locale]}${path}`;

  const origin = new URL(process.env.NEXT_PUBLIC_ORIGIN ?? process.env.NX_NEXT_PUBLIC_ORIGIN ?? '');

  if (customHost) {
    origin.host = customHost;
  }

  origin.pathname = canonicalPath;

  const link = origin.toString();

  const isElectricityTariffApp: boolean = getenv('APP', process.env.APP) === 'electricity-tariff';

  const parsedLink = cleanLink(link, isElectricityTariffApp);

  return parsedLink;
}

/**
 * A function that creates a meta description for the car detail page.
 * This is hardcoded in German because it is only used for the German version of the website.
 */
export function getCarDetailPageTitle(page: CarDetailPage) {
  return `${page.manufacturer} ${page.model} laden | Wallbox & Ladekabel für ${page.manufacturer} ${page.model} kaufen`;
}
